import axios from "axios";

import { isMockEnabled } from "../lib/mock";
import {
  FacebookAdAccount,
  FacebookDataset,
  FacebookPage,
  FacebookPageFeedItem,
} from "../types/facebook";
import { IntegrationSchema } from "../types/integration";
import {
  LinkedinAdAccount,
  LinkedinAudienceCount,
  LinkedinCampaign,
  LinkedinCampaignStatus,
} from "../types/linkedin";

export const getIntegrations = async (
  workspaceSlug: string,
): Promise<IntegrationSchema[]> => {
  return (await axios.get(`/api/v1/workspaces/${workspaceSlug}/integrations`))
    .data;
};

export const connectIntegration = async (
  workspaceSlug: string,
  integration: string,
  context: Record<string, any>,
): Promise<Record<string, any>> => {
  return (
    await axios.post(
      `/api/v1/workspaces/${workspaceSlug}/integrations/${integration}`,
      { context },
    )
  ).data;
};

export const disconnectIntegration = async (
  workspaceSlug: string,
  integration: string,
): Promise<void> => {
  await axios.delete(
    `/api/v1/workspaces/${workspaceSlug}/integrations/${integration}`,
  );
};

export const getFacebookAdAccounts = async (
  workspaceSlug: string,
): Promise<FacebookAdAccount[]> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/facebook/ad-accounts`,
    )
  ).data;
};

export const getFacebookDatasets = async (
  workspaceSlug: string,
  adAccountId: string,
): Promise<FacebookDataset[]> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/facebook/ad-accounts/${adAccountId}/datasets`,
    )
  ).data;
};

export const validateFacebookCapiToken = async (
  workspaceSlug: string,
  datasetId: string,
  accessToken: string,
): Promise<void> => {
  await axios.post(
    `/api/v1/workspaces/${workspaceSlug}/integrations/facebook-capi/validate-token`,
    { "dataset-id": datasetId, "access-token": accessToken },
  );
};

export const getFacebookPages = async (
  workspaceSlug: string,
): Promise<FacebookPage[]> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/facebook/pages`,
    )
  ).data;
};

export const getFacebookPageFeed = async (
  page: FacebookPage,
): Promise<FacebookPageFeedItem[]> => {
  return (
    await axios.get(`https://graph.facebook.com/v24.0/${page.id}/feed`, {
      params: { access_token: page.access_token },
    })
  ).data.data;
};

export const createFacebookCustomConversion = async (
  workspaceSlug: string,
  data: {
    name: string;
    "ad-account-id": string;
    "event-source-id": string;
    "conversion-event": string;
  },
): Promise<{ id: string }> => {
  return (
    await axios.post(
      `/api/v1/workspaces/${workspaceSlug}/integrations/facebook/custom-conversions`,
      data,
    )
  ).data;
};

export const getLinkedinAdAccounts = async (
  workspaceSlug: string,
): Promise<LinkedinAdAccount[]> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/linkedin/ad-accounts`,
    )
  ).data;
};

const campaignsResource = (): string =>
  isMockEnabled() ? "mock-campaigns" : "campaigns";

export interface GetLinkedinCampaignsOptions {
  statuses?: LinkedinCampaignStatus[];
}

export const getLinkedinCampaigns = async (
  workspaceSlug: string,
  adAccountId: number,
  { statuses }: GetLinkedinCampaignsOptions = {},
): Promise<LinkedinCampaign[]> => {
  const params = new URLSearchParams();
  if (statuses !== undefined) {
    params.set("status", statuses.join(","));
  }
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/linkedin/ad-accounts/${adAccountId}/${campaignsResource()}`,
      { params },
    )
  ).data;
};

export const getLinkedinCampaign = async (
  workspaceSlug: string,
  adAccountId: number,
  campaignId: number,
): Promise<LinkedinCampaign> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/linkedin/ad-accounts/${adAccountId}/${campaignsResource()}/${campaignId}`,
    )
  ).data;
};

export const getLinkedinCampaignAudienceCount = async (
  workspaceSlug: string,
  adAccountId: number,
  campaignId: number,
): Promise<LinkedinAudienceCount> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/linkedin/ad-accounts/${adAccountId}/${campaignsResource()}/${campaignId}/audience-count`,
    )
  ).data;
};
